import { shadowDirection, shadowLengthMetres } from "./sun"

// Computes the ground-shadow polygon for a rectangular object with an
// optional rotation, cast by the sun at (azimuth, altitude).
//
// Object shape in store: { xM, yM, widthM, heightM, rotationDeg, heightMetresAboveGround }
// - (xM, yM) is the top-left corner in local metres (east, south).
// - rotationDeg rotates the rectangle about its top-left corner (matches
//   Konva.Rect default).
//
// Shadow = convex hull of {bottom footprint} ∪ {top footprint offset by
// shadow vector}. For sun behind an upright box this degenerates to 4–6
// vertices; convex hull handles all angles uniformly.

export function computeShadowPolygon(obj, azimuth, altitude) {
  const H = obj.heightMetresAboveGround ?? 0
  const L = shadowLengthMetres(altitude, H)
  if (!L) return null

  const { dx, dy } = shadowDirection(azimuth)
  const offsetX = L * dx
  const offsetY = L * dy

  const footprint = rectCorners(obj)
  const topShadow = footprint.map(([x, y]) => [x + offsetX, y + offsetY])
  const hull = convexHull([...footprint, ...topShadow])
  return hull
}

// Returns the 4 corners of a rotated rectangle in local metres, in
// clockwise order starting top-left.
export function rectCorners({ xM, yM, widthM, heightM, rotationDeg = 0 }) {
  const rad = (rotationDeg * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  // Corners relative to (xM, yM) before rotation.
  const local = [[0, 0], [widthM, 0], [widthM, heightM], [0, heightM]]
  return local.map(([dx, dy]) => [xM + dx * cos - dy * sin, yM + dx * sin + dy * cos])
}

// Andrew's monotone chain convex hull, O(n log n). Returns polygon
// vertices in counter-clockwise order.
// The cross-product test uses `<= 0` (not `< 0`) deliberately: it drops
// collinear intermediate points, leaving only the hull's true corners.
// For our use (shadow = footprint ∪ offset-footprint of a box) this yields
// cleaner 4-6 vertex polygons and avoids degenerate edges.
export function convexHull(points) {
  if (points.length < 3) return points.slice()
  const pts = points.slice().sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]))
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])

  const lower = []
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop()
    lower.push(p)
  }
  const upper = []
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i]
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop()
    upper.push(p)
  }
  upper.pop(); lower.pop()
  return lower.concat(upper)
}
